import React, { useMemo, useState } from "react";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { StepProgress } from "./StepProgress";
import { CompletionIndicator } from "./CompletionIndicator";
import { ConfirmationScreen } from "./ConfirmationScreen";
import { ApplicationProvider, useApplication } from "./ApplicationProvider";
import { ErrorBoundary } from "./ErrorBoundary";
import { SaveAndContinueButton } from "./SaveAndContinueButton";
import { Step1Program } from "./steps/Step1Program";
import { Step2BasicInfo } from "./steps/Step2BasicInfo";
import { Step3Health } from "./steps/Step3Health";
import { Step4Insurance } from "./steps/Step4Insurance";
import { Step5Certifications } from "./steps/Step5Certifications";
import { Step6Review } from "./steps/Step6Review";
import {
  step1Schema,
  step1BasicInfoSchema,
  step2EligibilitySchema,
  emergencyContactSchema,
  step1GateComplete,
  step3HealthHistorySchema,
  step4InsuranceSchema,
  step5CertificationsSchema,
  physicalDateIsValid,
  stepCompletionFlags,
  phoneErrorsForStep,
  missingUploadsForStep,
} from "../validation";
import { mailingAddressIssues } from "../mailingAddress";
import { usePublicConfig } from "./PublicConfigProvider";
import type { z } from "zod";

function zodErrorsToMap(err: z.ZodError): Record<string, string> {
  const map: Record<string, string> = {};
  for (const issue of err.issues) {
    const path = issue.path.join(".");
    if (!map[path]) map[path] = issue.message;
  }
  return map;
}

function AppInner() {
  const cfg = usePublicConfig();
  const {
    data,
    setStep,
    update,
    completionPercent,
    hydrating,
    hydrateError,
    saveState,
    saveNow,
    ensureDraftOutcome,
  } = useApplication();
  // Inline field errors are stored per step and only populated once the applicant
  // has left a step (treated as "I'm done here, check it") — so the form doesn't
  // light up red on first load. The non-blocking banner below is computed live.
  const [errorsByStep, setErrorsByStep] = useState<Record<number, Record<string, string>>>({});
  const [submitState, setSubmitState] = useState<"idle" | "submitting" | "error">("idle");
  const [submitMessage, setSubmitMessage] = useState<string>("");
  // Round 16 — Step 1 is a deliberate START GATE (the only gate in the form).
  // The applicant cannot leave Step 1 until the basic-information cluster is
  // complete AND the server has confirmed their student email is not a duplicate
  // of an existing application (the email is the application's identity/key).
  // Steps 2–6 keep FREE NAVIGATION — there are no gates anywhere past Step 1.
  const [gateChecking, setGateChecking] = useState(false);
  const [gateBlocked, setGateBlocked] = useState(false);

  const step = data.meta.currentStep;

  // Required uploads missing on a step, keyed so they surface in that step's
  // inline errors and the "still needs" banner — consistent with the Step 6
  // submission gate and the step-completeness badge.
  const uploadErrorMap = (s: number): Record<string, string> => {
    const map: Record<string, string> = {};
    for (const u of missingUploadsForStep(data, s)) {
      map[`upload:${u.key}`] = `Upload still needed: ${u.label}`;
    }
    return map;
  };

  // Field-level error map for a given step (same checks that used to gate Next),
  // plus any required uploads still missing on that step.
  const computeStepErrorMap = (s: number): Record<string, string> => {
    if (s === 1) {
      // Step 1 (Basic Information) draws its required fields from both step1
      // (session year + attestation) and the basic-info cluster that moved up
      // from step2 (emails, identity, address, Family Information).
      const s1 = step1Schema.safeParse(data.step1);
      const s1b = step1BasicInfoSchema.safeParse(data.step2);
      // Round 22 (R-02) — the per-type mailing-address rules must ALSO run
      // standalone. They live in step1BasicInfoSchema's superRefine, which Zod
      // skips entirely while the object parse aborts on any other blank required
      // field — so on a partly-filled Step 1 the address rules would never
      // surface. Same Round 14 gotcha as the student/parent email check.
      const addressErrors: Record<string, string> = {};
      for (const issue of mailingAddressIssues(data.step2)) {
        addressErrors[issue.path] ??= issue.message;
      }
      return {
        ...(s1.success ? {} : zodErrorsToMap(s1.error)),
        ...(s1b.success ? {} : zodErrorsToMap(s1b.error)),
        ...addressErrors,
        // Round 24 — phone format depends on addressType, so it lives in
        // phoneIssues rather than the schema, and needs the same standalone
        // call for the same reason addressErrors above does.
        ...phoneErrorsForStep(data, 1),
      };
    }
    if (s === 2) {
      // Step 2 (Eligibility): citizenship/residency + proof and school + cert.
      const parsed = step2EligibilitySchema.safeParse(data.step2);
      return { ...(parsed.success ? {} : zodErrorsToMap(parsed.error)), ...uploadErrorMap(2) };
    }
    if (s === 3) {
      const base = (() => {
        const parsed = step3HealthHistorySchema.safeParse(data.step3);
        const emergency = emergencyContactSchema.safeParse(data.step2);
        const map = {
          ...(parsed.success ? {} : zodErrorsToMap(parsed.error)),
          // Emergency Contact moved to Step 3; its fields still live on step2.
          ...(emergency.success ? {} : zodErrorsToMap(emergency.error)),
        };
        if (
          parsed.success &&
          !physicalDateIsValid(data.step3.lastPhysicalDate, cfg.sessionStartDate)
        ) {
          map.lastPhysicalDate = `Physical exam date must be within 12 months of session start (${cfg.sessionStartDate}).`;
        }
        return map;
      })();
      return { ...base, ...phoneErrorsForStep(data, 3), ...uploadErrorMap(3) };
    }
    if (s === 4) {
      const parsed = step4InsuranceSchema.safeParse(data.step4);
      return {
        ...(parsed.success ? {} : zodErrorsToMap(parsed.error)),
        ...phoneErrorsForStep(data, 4),
        ...uploadErrorMap(4),
      };
    }
    if (s === 5) {
      const parsed = step5CertificationsSchema.safeParse(data.step5);
      return { ...(parsed.success ? {} : zodErrorsToMap(parsed.error)), ...uploadErrorMap(5) };
    }
    return {};
  };

  // Live "this section still needs" list for the current step (informational).
  const liveErrors = useMemo(
    () => computeStepErrorMap(step),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, step, cfg.sessionStartDate]
  );

  const completion = useMemo(
    () => stepCompletionFlags(data, cfg.sessionStartDate),
    [data, cfg.sessionStartDate]
  );

  // Resuming via a magic link: show a loading screen while the draft is
  // fetched. IMPORTANT (Rules of Hooks): this early return must stay BELOW
  // every hook call in this component — putting it above a hook changes the
  // hook order between the loading render and the post-hydration render and
  // crashes React to a blank page.
  if (hydrating) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 max-w-4xl w-full mx-auto px-4 py-16">
          <div className="section-card text-center">
            <p className="text-sm text-slate-600">Loading your saved application…</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const errors = errorsByStep[step] ?? {};

  // Leaving a step records its current errors so inline messages are visible if
  // the applicant returns. Navigation past Steps 2–6 is never blocked; only
  // Step 1 is gated (see below).
  const leaveStep = (fromStep: number) => {
    setErrorsByStep((prev) => ({ ...prev, [fromStep]: computeStepErrorMap(fromStep) }));
  };

  // The Step 1 hard gate. Returns true when the applicant may leave Step 1. The
  // local condition (attestation + emails + identity + address + Family) is
  // synchronous; the duplicate-email check is authoritative and server-side, so
  // we AWAIT it before allowing exit (Round 15 full paginated scan). A resuming
  // or amending user holds a draft token, so the server returns their own draft
  // (token) rather than a conflict — they are never trapped here.
  const passesStep1Gate = async (): Promise<boolean> => {
    if (!step1GateComplete(data)) {
      setGateBlocked(true);
      return false;
    }
    setGateChecking(true);
    try {
      const outcome = await ensureDraftOutcome();
      // Block ONLY on a definitive duplicate. Transient server/offline errors
      // must not trap the applicant — their work is saved locally and the
      // duplicate guard is re-enforced server-side at submit.
      if (outcome.conflict) {
        setGateBlocked(true);
        return false;
      }
      setGateBlocked(false);
      return true;
    } finally {
      setGateChecking(false);
    }
  };

  const goTo = async (target: number) => {
    if (step === 1 && target !== 1) {
      leaveStep(1);
      const ok = await passesStep1Gate();
      if (!ok) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
    } else {
      leaveStep(step);
    }
    setGateBlocked(false);
    setStep(target);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onNext = () => void goTo(Math.min(step + 1, 6));
  const onBack = () => void goTo(Math.max(step - 1, 1));
  const onJump = (s: number) => void goTo(s);

  const onSubmit = async () => {
    if (submitState === "submitting") return; // double-click guard
    setSubmitState("submitting");
    try {
      // Push the latest data to the server draft, then run the real submission
      // pipeline (reference number, PDFs, applicant folder, tracker, emails).
      const token = await saveNow();
      if (!token) {
        setSubmitState("error");
        setSubmitMessage(
          "Could not reach the server to submit. Your work is saved on this device — please try again shortly."
        );
        return;
      }
      const res = await fetch(`/api/drafts/${encodeURIComponent(token)}/submit`, { method: "POST" });
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        referenceNumber?: string;
        status?: typeof data.meta.status;
        submittedAt?: string;
        lastAmendedAt?: string | null;
        amendmentCount?: number;
        confirmationEmailSent?: boolean;
      };
      if (!res.ok) {
        setSubmitState("error");
        setSubmitMessage(body.error || "Could not submit. Please try again later.");
        return;
      }
      update((d) => {
        d.meta.status = body.status ?? "Submitted";
        d.meta.submittedAt = body.submittedAt ?? new Date().toISOString();
        d.meta.referenceNumber = body.referenceNumber ?? d.meta.referenceNumber;
        d.meta.amendmentCount = body.amendmentCount ?? d.meta.amendmentCount;
        d.meta.lastAmendedAt = body.lastAmendedAt ?? d.meta.lastAmendedAt;
        d.meta.confirmationEmailSent = body.confirmationEmailSent;
      });
      setSubmitState("idle");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setSubmitState("error");
      setSubmitMessage("Could not submit right now. Your work is saved — please try again shortly.");
    }
  };

  // After submission the applicant returns via their magic link (locally: the
  // draft persists with status "Submitted"/"Finalized") to the sponsor step.
  if (data.meta.status === "Submitted" || data.meta.status === "Finalized") {
    return <ConfirmationScreen />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 py-6">
        {hydrateError && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm text-amber-900">
              We couldn't open that resume link — it may have expired or already been used. You can
              continue with the application on this device below.
            </p>
          </div>
        )}
        {saveState === "conflict" && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm text-amber-900">
              A saved application already exists for <strong>{data.step2.studentEmail}</strong>. Each
              applicant must use their own unique email address — siblings and twins in the same
              family each need a separate email, and this should be the student's own email, not a
              parent's. If you're a parent completing this for your child, enter the student's email
              address here. We've emailed the existing application's resume link to the addresses on
              file so it can be continued. (Edits on this page are kept on this device only until
              then.)
            </p>
          </div>
        )}
        {data.meta.status === "AmendmentInProgress" && (
          <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-3">
            <p className="text-sm text-blue-900">
              You are amending a submitted application
              {data.meta.referenceNumber && (
                <>
                  {" "}(reference <strong>{data.meta.referenceNumber}</strong>)
                </>
              )}
              . Make your corrections, then resubmit from the Review step — your reference number
              stays the same.
            </p>
          </div>
        )}
        <CompletionIndicator completionPercent={completionPercent ?? 0} status={data.meta.status} />
        <StepProgress currentStep={step} onJump={onJump} completion={completion} />

        {step === 1 && <Step1Program errors={errors} />}
        {step === 2 && <Step2BasicInfo errors={errors} />}
        {step === 3 && <Step3Health errors={errors} />}
        {step === 4 && <Step4Insurance errors={errors} />}
        {step === 5 && <Step5Certifications errors={errors} />}
        {step === 6 && (
          <Step6Review
            onJump={(s) => goTo(s)}
            onSubmit={onSubmit}
            submitting={submitState === "submitting"}
          />
        )}

        {step < 6 && Object.keys(liveErrors).length > 0 && (
          <div className="mt-6 rounded-md border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm font-medium text-amber-900">
              {/* Step 1 is the hard gate, so the free-nav promise would be false
                  there; Steps 2–6 keep it (navigation is never blocked past Step 1). */}
              {step === 1
                ? "Step 1 must be completed before you can continue. Still required:"
                : "This section still needs the following (you can continue and come back):"}
            </p>
            <ul className="mt-1 list-disc list-inside text-sm text-amber-800">
              {Array.from(new Set(Object.values(liveErrors))).map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </div>
        )}

        {step === 1 && gateBlocked && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-900">
              Please complete all required Basic Information above (including a valid, unique student
              email that differs from the parent/guardian email) before continuing. This first step
              must be finished to proceed; the remaining steps can be completed in any order.
            </p>
          </div>
        )}

        {step < 6 && (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <button type="button" onClick={onBack} disabled={step === 1} className="btn-secondary">
              Back
            </button>
            {step >= 2 && <SaveAndContinueButton />}
            <button
              type="button"
              onClick={onNext}
              disabled={gateChecking}
              className="btn-primary"
            >
              {gateChecking ? "Checking…" : "Next"}
            </button>
          </div>
        )}

        {submitState === "error" && (
          <p className="mt-3 text-sm text-red-600">{submitMessage}</p>
        )}
      </main>
      <Footer />
    </div>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <ApplicationProvider>
        <AppInner />
      </ApplicationProvider>
    </ErrorBoundary>
  );
}
